import { FC, useContext, useState, useEffect } from "react";
import { Table, Badge, Button, Spinner } from "react-bootstrap";
import moment from "moment";
import apiKeyApi, { AdminApiKey } from "../../../api/apiKeyApi";
import { AppContext } from "../../../context";

const creatorName = (key: AdminApiKey) => {
  if (!key.createdBy) return "—";
  const name = `${key.createdBy.firstName ?? ""} ${key.createdBy.lastName ?? ""}`.trim();
  return name || key.createdBy.email;
};

interface Props {
  companyId: string;
}

const CompanyApiKeys: FC<Props> = ({ companyId }) => {
  const context = useContext(AppContext);
  const [keys, setKeys] = useState<AdminApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);

  const fetchKeys = async () => {
    try {
      setLoading(true);
      const { data } = await apiKeyApi.list(companyId);
      setKeys(data);
      setLoading(false);
    } catch {
      setLoading(false);
      context.showToast({
        title: "Fetch failed",
        message: "Could not load API keys",
        variant: "danger",
      });
    }
  };

  useEffect(() => {
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleRevoke = async (key: AdminApiKey) => {
    const confirmed = window.confirm(`Revoke key "${key.label}" (${key.maskedKey})? This cannot be undone.`);
    if (!confirmed) return;
    try {
      setRevoking(key.id);
      await apiKeyApi.revoke(key.id);
      context.showToast({
        title: "Key revoked",
        message: `"${key.label}" has been revoked`,
        variant: "success",
      });
      await fetchKeys();
    } catch {
      context.showToast({
        title: "Revoke failed",
        message: "Try again",
        variant: "danger",
      });
    } finally {
      setRevoking(null);
    }
  };

  if (loading && !keys.length) {
    return (
      <div className="d-flex justify-content-center mt-4">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="pt-3">
      <Table striped hover responsive>
        <thead>
          <tr>
            <th>Label</th>
            <th>Key</th>
            <th>Created by</th>
            <th>Created</th>
            <th>Last used</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const isRevoked = k.revokedAt !== null;
            return (
              <tr key={k.id}>
                <td>{k.label}</td>
                <td>
                  <code>{k.maskedKey}</code>
                </td>
                <td>{creatorName(k)}</td>
                <td>{moment(k.createdAt).format("D.M.YYYY")}</td>
                <td>
                  {k.lastUsedAt ? (
                    moment(k.lastUsedAt).format("D.M.YYYY HH:mm")
                  ) : (
                    <span className="text-muted">Never</span>
                  )}
                </td>
                <td>
                  {isRevoked ? (
                    <Badge bg="danger">Revoked{k.revokedBy ? ` · ${k.revokedBy}` : ""}</Badge>
                  ) : (
                    <Badge bg="success">Active</Badge>
                  )}
                </td>
                <td>
                  {!isRevoked && (
                    <Button size="sm" variant="danger" disabled={revoking === k.id} onClick={() => handleRevoke(k)}>
                      {revoking === k.id ? <Spinner size="sm" animation="border" /> : "Revoke"}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
          {!loading && keys.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                No API keys for this company
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default CompanyApiKeys;
